import type { ResourceArray } from '@medplum/core'
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import type { SeenMedplumClient } from '@seen/core/client'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import {
  skipToken,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export type UseFhirQueryOptions<T extends ResourceType> = UseQueryOptions<
  ResourceArray<ExtractResource<T>>,
  Error,
  ResourceArray<ExtractResource<T>>,
  ReturnType<(typeof cacheKeys)['search']>
>

interface UseFhirSearchOptions<T extends ResourceType> {
  query?: Omit<
    UseFhirQueryOptions<T>,
    'initialData' | 'queryKey' | 'queryFn' | 'keepPreviousData'
  >
}

/**
 * @param type - The resource type.
 * @param query - The query.
 * @param options - The options.
 * @returns The query result.
 * @deprecated Use useSearch instead
 */
export function useFhirSearch<T extends ResourceType>(
  type: T,
  query?: QueryTypes,
  options?: UseFhirSearchOptions<T> | null
): UseQueryResult<ResourceArray<ExtractResource<T>>> {
  const medplum = useMedplum()

  const result = useQuery(
    getFhirSearchQueryOptions({ type, query, options, medplum })
  )

  useErrorToast(
    result.error &&
      `Error accessing ${getSearchKey(medplum, type, normalizeQuery(query))}: ${result.error.message ?? 'Unknown error'}`
  )

  return result
}

export function getFhirSearchQueryOptions<T extends ResourceType>({
  type,
  query,
  options,
  medplum,
}: {
  type: T
  query?: QueryTypes
  options?: UseFhirSearchOptions<T> | null | undefined
  medplum: SeenMedplumClient
}): UseFhirQueryOptions<T> {
  const parameters = normalizeQuery(query)

  const searchKey = query ? getSearchKey(medplum, type, parameters) : NO_QUERY

  return {
    ...options?.query,
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.search(type, searchKey),
    meta: {
      ...options?.query?.meta,
      queryParams: parameters,
      resourceType: type,
    },
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : () => medplum.searchResources<T>(type, parameters),
  }
}
